import store from '../store';

export class TimeoutError extends Error {
  constructor(...args) {
    super(...args);
    Error.captureStackTrace(this, TimeoutError);
  }
}

const selectBearer = state => {
  try {
    return state.oidc.user.access_token;
  } catch (e) {
    return null;
  }
};

const getHeaders = () => ({
  Authorization: `Bearer ${selectBearer(store.getState())}`,
  Accept: 'application/json',
  'Content-Type': 'application/json',
});

// All requests go to the Smart Cabinet Root API. The old core API (formerly REACT_APP_IPA_API) is no longer called.
const ROOT_API_URL = process.env.REACT_APP_ROOT_API;

const fetchWithTimeout = async (url, params, timeout = 10000) => {
  const controller = new AbortController();
  const signal = controller.signal;
  setTimeout(() => controller.abort(), timeout);
  return fetch(url, {
    ...params,
    signal,
  }).catch(error => {
    if (error.name === 'AbortError') {
      throw new TimeoutError('Request timed out');
    }
    throw error;
  });
};

export const getReleaseVersion = () =>
  fetchWithTimeout(`${ROOT_API_URL}/api/v2/release-version`, {
    method: 'GET',
    headers: getHeaders(),
  });

export const getUserInformation = () =>
  fetchWithTimeout(`${ROOT_API_URL}/api/v4/me`, {
    method: 'GET',
    headers: getHeaders(),
  });

export const putConfiguration = (deviceId, configuration) =>
  fetchWithTimeout(
    `${ROOT_API_URL}/api/v3/provisioning/configure/${deviceId}`,
    {
      method: 'PUT',
      body: JSON.stringify(configuration),
      headers: getHeaders(),
    }
  );

// Merges the given keys into the cabinet settings (replaces core API POST /v3/Devices/{code}/alternative_id)
export const postSettings = (deviceCode, settings) =>
  fetchWithTimeout(`${ROOT_API_URL}/api/v3/device-settings/${deviceCode}`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
    headers: getHeaders(),
  });

export const getConfiguration = deviceId => {
  return fetchWithTimeout(`${ROOT_API_URL}/api/v4/graphql`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      query: `{ device(hardwareId:"${deviceId}") { type subType name code owners { id } osVersion lifeCycleStatus } }`,
    }),
  });
};

export const requestReplacement = (targetId, replacementId) => {
  return fetchWithTimeout(`${ROOT_API_URL}/api/v3/provisioning/replace`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      targetId,
      replacementId,
    }),
  });
};

export const requestRetire = (targetId, retiredReasonId = null) => {
  // console.log('retire reason id: ' + retiredReasonId);
  return fetchWithTimeout(`${ROOT_API_URL}/api/v3/provisioning/retire`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      targetId,
      retiredReasonId,
    }),
  });
};

export const requestReprovisionDevice = configuration => {
  return fetchWithTimeout(`${ROOT_API_URL}/api/v3/provisioning/reconfigure`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ configuration }),
  });
};

export const requestEnableFAT = (deviceCode, group) => {
  const url = group
    ? `${ROOT_API_URL}/api/v3/provisioning/enableFAT/${deviceCode}?group=${encodeURIComponent(
        group
      )}`
    : `${ROOT_API_URL}/api/v3/provisioning/enableFAT/${deviceCode}`;
  return fetchWithTimeout(url, {
    method: 'PUT',
    headers: getHeaders(),
  });
};

export const requestEnableOAT = (deviceCode, group) => {
  const url = group
    ? `${ROOT_API_URL}/api/v3/provisioning/startOAT/${deviceCode}?group=${encodeURIComponent(
        group
      )}`
    : `${ROOT_API_URL}/api/v3/provisioning/startOAT/${deviceCode}`;
  return fetchWithTimeout(url, {
    method: 'PUT',
    headers: getHeaders(),
  });
};

export const requestSendOATResults = oatResults => {
  const { deviceCode, organization } = oatResults;
  const url = organization
    ? `${ROOT_API_URL}/api/v3/provisioning/endOAT/${deviceCode}?group=${encodeURIComponent(
        organization
      )}`
    : `${ROOT_API_URL}/api/v3/provisioning/endOAT/${deviceCode}`;
  return fetchWithTimeout(url, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ oatResults }),
  });
};

export const postCommand = (deviceCode, command, params) => {
  const payload = {
    deviceCode,
    command,
    params,
  };
  const url = `${ROOT_API_URL}/api/v1/command`;
  return fetchWithTimeout(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
};
